import { createComputed } from "solid-js";
import { getRequestEvent } from "solid-js/web";
import { Record as _Record } from "record-tuple";
import { getCookiesString, parseCookie } from "@solid-primitives/cookies";

import UCrypto from "@repo/utils/UCrypto";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import Platform from "@repo/utils-client/Platform";

import { AnalyticsEvents, Registration } from "~/analytics/client";
import appSettings from "~/appSettings";
import { useUser } from "~/state/features/user";
import Marketing from "~/utils/Marketing";

const id = appSettings.marketing.snapchatId;

declare global {
  interface Window {
    snaptr: (...args: any[]) => void;
  }
}

const initialUrl = Platform.isClient && window.location.href;

export default async (): Promise<Registration.Conversion> => {
  if (!id) return;
  if (import.meta.env.APP_ENV !== "production") return;

  // prettier-ignore
  // @ts-expect-error
  // eslint-disable-next-line
  (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';var r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');

  window.snaptr("track", "PAGE_VIEW");

  const init = UFunction.memo(
    (userParams: Record<string, string>) => {
      window.snaptr("init", id, userParams);
    },
    { by: ([userParams]) => _Record(userParams) }
  );

  init({});

  const eventByName: Record<keyof AnalyticsEvents["conversion"], string> = {
    "Credit Account Open": "PURCHASE",
    "Make Credit Line Payment": "SUBSCRIBE",
    Signup: "SIGN_UP",
    SnapchatOnlyLogin: "LOGIN",
  };

  return {
    identify() {
      const [user] = useUser();

      createComputed(() => {
        const pii = user.pii();
        if (pii)
          // https://businesshelp.snapchat.com/s/article/pixel-direct-implementation?language=en_US
          init({
            user_email: pii.email,
            user_phone_number: pii.phone,
            firstname: pii.name.first,
            lastname: pii.name.last,
            geo_city: pii.address.locality,
            geo_state: pii.address.subdivision,
            geo_country: pii.address.country,
            geo_postal_code: pii.address.postalCode,
          });
        else init({});
      });
    },
    anonymize() {
      init({});
      init.invalidate.all();
    },
    async track(name, properties) {
      const event = eventByName[name];
      if (!event) return;

      const [user] = useUser();

      const tier = Marketing.useTier(properties);
      const expectedLtv = Marketing.useExpectedLtv(properties);

      const payload = {
        item_ids: [tier],
        item_category: "SUBSCRIPTION",
        price: expectedLtv,
        currency: appSettings.currency,
        number_items: 1,
        transaction_id: properties.accountId,
      };
      window.snaptr("track", event, payload);
      sendServerEvent(event, {
        ...payload,
        event_source_url: initialUrl,
        timestamp: Date.now(),
        uuid_c1: user.id(),
        email: user.pii()?.email,
        phone: user.pii()?.phone,
      });
    },
  };
};

async function sendServerEvent(event: string, properties: Record<string, any>) {
  const authToken =
    import.meta.env.API_ADAPTER === "us" &&
    "eyJhbGciOiJIUzI1NiIsImtpZCI6IkNhbnZhc1MyU0hNQUNQcm9kIiwidHlwIjoiSldUIn0.eyJhdWQiOiJjYW52YXMtY2FudmFzYXBpIiwiaXNzIjoiY2FudmFzLXMyc3Rva2VuIiwibmJmIjoxNjk1ODQyMTgzLCJzdWIiOiI4M2NhOThmZi00ZjVhLTQxYjItYmQxNy0zZWRiY2U3MjYyMGR-UFJPRFVDVElPTn4wZDc2NzU2Yy02MzRkLTQwMTgtOTAzNS1iYWY1YTVmMzZiNzgifQ.6M-77gDX8jkBSj192CiUaxMpWxE5Q1qBlAvzDBzEF18";

  if (!authToken) return;

  const hashes = await UObject.transformAsync(
    {
      email: properties.email,
      phone: properties.phone,
      ip: getRequestEvent()?.clientAddress,
    },
    async ([key, value]) => [key, await UCrypto.sha256Hex(value)]
  );

  fetch("https://snaptr.com/server-event", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      pixel_id: id,
      event_type: event,
      event_conversion_type: "WEB",
      hashed_email: hashes.email,
      hashed_phone: hashes.phone,
      hashed_ip: hashes.ip,
      user_agent: getRequestEvent()?.request.headers.get("user-agent"),
      sc_cookie1: parseCookie(getCookiesString(), "_scid"),
      ...UObject.omit(properties, ["email", "phone"]),
    }),
  });
}
